/**
 * `/p/:slug` — the pad. Single screen hosting all states (spec.md § Routing &
 * pages). This route:
 *   1. Fetches the pad (skeleton while loading; not-found page on 404/410).
 *   2. Renders the top bar (slug, expiry, Save, `…` menu) around the editor.
 *   3. Owns the Save state reported by the editor and the `…` menu's actions
 *      (copy markdown, copy link, delete) wired to the api client + toasts.
 *
 * The editor below is Slice B's real `<PadEditor>` (default export matching
 * `PadEditorProps`), imported from `../editor/PadEditor`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { GetPadResponse } from "@shared/types/api";
import { deletePad, getPad, PadNotFoundError } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { NotFound } from "../components/NotFound";
import { PadSkeleton } from "../components/PadSkeleton";
import { TopBar } from "../components/TopBar";
import { useToast } from "../components/ShellToast";
import PadEditor from "../editor/PadEditor";
import type { PadEditorHandle, SaveState } from "../components/padEditorContract";

type LoadState =
  | { status: "loading" }
  | { status: "loaded"; pad: GetPadResponse }
  | { status: "not-found" };

export default function PadRoute(): React.JSX.Element {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { controller: toast, element: toastElement } = useToast();

  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [saveState, setSaveState] = useState<SaveState>("clean");
  const editorRef = useRef<PadEditorHandle | null>(null);

  // Fetch the pad on mount / slug change.
  useEffect(() => {
    let cancelled = false;
    setLoad({ status: "loading" });

    async function fetchPad(): Promise<void> {
      try {
        const pad = await getPad(slug);
        if (!cancelled) {
          setLoad({ status: "loaded", pad });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof PadNotFoundError) {
          setLoad({ status: "not-found" });
        } else {
          // Network / 5xx: surface the not-found page as the safe fallback so
          // the user always has a "Start a new pad" escape hatch.
          setLoad({ status: "not-found" });
        }
      }
    }

    void fetchPad();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleReady = useCallback((handle: PadEditorHandle) => {
    editorRef.current = handle;
  }, []);

  const handleSave = useCallback(() => {
    editorRef.current?.save();
  }, []);

  // Cmd/Ctrl+S triggers a save (spec.md § Editor). The shell owns the global
  // shortcut; the editor performs the save via its handle.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        handleSave();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [handleSave]);

  const handleCopyMarkdown = useCallback(async () => {
    const body = editorRef.current?.getBody() ?? "";
    const ok = await copyText(body);
    toast.show(ok ? "Markdown copied" : "Couldn't copy to clipboard");
  }, [toast]);

  const handleCopyLink = useCallback(async () => {
    const ok = await copyText(window.location.href);
    toast.show(ok ? "Link copied" : "Couldn't copy to clipboard");
  }, [toast]);

  const handleDelete = useCallback(async () => {
    try {
      await deletePad(slug);
      toast.show("Pad deleted");
    } catch {
      // Even on failure the pad is effectively unreachable for this user; route
      // to the not-found page (a re-fetch would 404/410 anyway).
    }
    // The pad is gone — route to the not-found page (spec: DELETE → not-found).
    void navigate("/not-found", { replace: true });
  }, [navigate, slug, toast]);

  if (load.status === "loading") {
    return <PadSkeleton />;
  }

  if (load.status === "not-found") {
    return <NotFound />;
  }

  const { pad } = load;

  return (
    <div className="min-h-screen">
      <TopBar
        slug={pad.slug}
        expiresAt={pad.expiresAt}
        saveState={saveState}
        onSave={handleSave}
        menuActions={{
          onCopyMarkdown: () => {
            void handleCopyMarkdown();
          },
          onCopyLink: () => {
            void handleCopyLink();
          },
          onDelete: () => {
            void handleDelete();
          },
        }}
      />
      <main>
        <PadEditor pad={pad} onSaveStateChange={setSaveState} onReady={handleReady} />
      </main>
      {toastElement}
    </div>
  );
}
